/**
 * ReviewStep - Step 5: Review all data and create project
 */

import React, { useState } from 'react';
import { logger } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../../contexts/ProjectContext';
import { useWizard } from '../../../contexts/WizardContext';
import { createProject } from '../../../api/projects';
import { createComponent } from '../../../api/components';
import {
  createCauseEffect,
  createComponentEffect,
  createRootCauseLink,
} from '../../../api/cause-effects';
import { showToast } from '@/components/Notifications/Toast';
import type { ScratchFormData } from '../ScratchDetailsScreen';
import { useTerminology } from '../../../hooks/useTerminology';
import { ComponentType, CauseEffectType, Category5M } from '@fmeastudio/shared';
import './ReviewStep.css';

interface ReviewStepProps {
  data: ScratchFormData;
  onBack: () => void;
  onCancel: () => void;
}

export function ReviewStep({ data, onBack, onCancel }: ReviewStepProps) {
  const navigate = useNavigate();
  const { setActiveProject } = useProject();
  const { cancelWizard } = useWizard();
  const { t } = useTerminology();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculatedRPN = data.rpn.severity * data.rpn.occurrence * data.rpn.detection;

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);

    try {
      // 1. Create project
      const project = await createProject({
        name: data.project.name,
        description: data.project.description || undefined,
        industry: data.project.industry || undefined,
      });

      // 2. Create component
      const component = await createComponent({
        projectId: project.id,
        name: data.component.name,
        type: data.component.type as ComponentType,
      });

      // 3. Create failure mode (cause-effect)
      const failure = await createCauseEffect({
        projectId: project.id,
        type: CauseEffectType.FAILURE_MODE,
        description: data.failure.description,
        category5m: data.failure.category as Category5M,
        severity: data.rpn.severity,
        occurrence: data.rpn.occurrence,
        detection: data.rpn.detection,
      });

      // 4. Create root cause
      const cause = await createCauseEffect({
        projectId: project.id,
        type: CauseEffectType.ROOT_CAUSE,
        description: data.failure.cause,
        category5m: data.failure.category as Category5M,
      });

      // 5. Link component to failure
      await createComponentEffect({
        componentId: component.id,
        causeEffectId: failure.id,
      });

      // 6. Link failure to root cause
      await createRootCauseLink({
        failureModeId: failure.id,
        rootCauseId: cause.id,
      });

      // 7. Set as active project
      setActiveProject(project);

      // 8. Close wizard and navigate
      cancelWizard();
      showToast.success('Project created successfully!');
      navigate('/');
    } catch (err) {
      logger.error('Error creating project', err, { module: 'ReviewStep' });
      setError(err instanceof Error ? err.message : 'Failed to create project. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <div className="step-container" data-testid="review-step">
      <div>
        <h2 className="step-title">Review and Create</h2>
        <p className="step-subtitle">
          Review your FMEA details below. Once created, you can add more components and failures in
          the main app.
        </p>
      </div>

      <div className="review-sections">
        {/* Project Summary */}
        <div className="review-section">
          <h3 className="review-section-title">Project</h3>
          <div className="review-item">
            <span className="review-label">Name:</span>
            <span className="review-value">{data.project.name}</span>
          </div>
          {data.project.description && (
            <div className="review-item">
              <span className="review-label">Description:</span>
              <span className="review-value">{data.project.description}</span>
            </div>
          )}
          {data.project.industry && (
            <div className="review-item">
              <span className="review-label">Industry:</span>
              <span className="review-value">{data.project.industry}</span>
            </div>
          )}
        </div>

        {/* Component Summary */}
        <div className="review-section">
          <h3 className="review-section-title">Component</h3>
          <div className="review-item">
            <span className="review-label">Name:</span>
            <span className="review-value">{data.component.name}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Type:</span>
            <span className="review-value">{data.component.type}</span>
          </div>
        </div>

        {/* Failure Summary */}
        <div className="review-section">
          <h3 className="review-section-title">{t.failureMode}</h3>
          <div className="review-item">
            <span className="review-label">Description:</span>
            <span className="review-value">{data.failure.description}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Cause:</span>
            <span className="review-value">{data.failure.cause}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Category:</span>
            <span className="review-value">{data.failure.category}</span>
          </div>
        </div>

        {/* RPN Summary */}
        <div className="review-section review-rpn">
          <h3 className="review-section-title">Risk Assessment</h3>
          <div className="review-item">
            <span className="review-label">Severity:</span>
            <span className="review-value">{data.rpn.severity}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Occurrence:</span>
            <span className="review-value">{data.rpn.occurrence}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Detection:</span>
            <span className="review-value">{data.rpn.detection}</span>
          </div>
          <div className="review-item review-rpn-total">
            <span className="review-label">{t.rpn}:</span>
            <span className="review-value review-rpn-value">{calculatedRPN}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" data-testid="error-message">
          {error}
        </div>
      )}

      <div className="step-actions">
        <div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
            disabled={isCreating}
            data-testid="back-button"
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isCreating}
            data-testid="cancel-button"
            style={{ marginLeft: '8px' }}
          >
            Cancel
          </button>
        </div>
        <button
          type="button"
          className="btn btn-success"
          onClick={handleCreate}
          disabled={isCreating}
          data-testid="create-button"
        >
          {isCreating ? 'Creating...' : 'Create Project'}
        </button>
      </div>
    </div>
  );
}
